import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Submit an MCQ answer
 * @param {Object} params
 * @param {string} params.topicId - Topic ObjectID hex string
 * @param {number} params.selectedAnswer - 0-based index of selected option
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Submission result with isCorrect, correctAnswer, score, etc.
 */
export const submitMcqAnswer = async ({ topicId, selectedAnswer }, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/submissions/mcq`,
      { topicId, selectedAnswer },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting MCQ answer:", error);
    throw error;
  }
};

/**
 * Submit a coding solution
 * @param {Object} params
 * @param {string} params.topicId - Topic ObjectID hex string
 * @param {string} params.code - Source code
 * @param {string} params.language - Language (go, python, java, javascript, c, cpp, rust, bash, typescript)
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Submission result with testResults, score, attemptsRemaining, etc.
 */
export const submitCodingSolution = async ({ topicId, code, language }, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/submissions/coding`,
      { topicId, code, language },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting coding solution:", error);
    throw error;
  }
};

/**
 * Fetch submission history for a topic
 * @param {string} topicId - Topic ObjectID hex string
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Object containing submissions array, bestScore, canSubmit, etc.
 */
export const fetchTopicSubmissions = async (topicId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/submissions/topic/${topicId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching topic submissions:", error);
    throw error;
  }
};

/**
 * Fetch all submissions for a chapter
 * @param {string} chapterId - Chapter ObjectID hex string
 * @param {string} token - JWT auth token
 * @returns {Promise<Array>} Array of submission objects
 */
export const fetchChapterSubmissions = async (chapterId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/submissions/chapter/${chapterId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chapter submissions:", error);
    throw error;
  }
};

/**
 * Clear all submissions for a chapter (reset progress)
 * @param {string} chapterId - Chapter ObjectID hex string
 * @param {string} token - JWT auth token
 * @returns {Promise<Object>} Response with deleted count
 */
export const clearChapterSubmissions = async (chapterId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/submissions/chapter/${chapterId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error clearing chapter submissions:", error);
    throw error;
  }
};
